import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProjects, getServices, getStats } from '../services/api';
import ProjectCard from '../components/ProjectCard';
import ServiceCard from '../components/ServiceCard';
import TypingEffect from '../components/TypingEffect';
import AIChatbot from './Dashboard';
import { Bot, Send, Menu, X as CloseIcon } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import './Home.css';

const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChatbot, setShowChatbot] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, servicesRes, statsRes] = await Promise.all([
          getProjects(),
          getServices(),
          getStats(),
        ]);
        
        setFeaturedProjects(projectsRes.data.filter(p => p.featured).slice(0, 3));
        setServices(servicesRes.data.slice(0, 3));
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (showChatbot) {
    return (
      <>
        <button 
          onClick={() => setShowChatbot(false)} 
          className="chatbot-back-btn"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            padding: '8px 16px',
            backgroundColor: '#1e3a5f',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px'
          }}
        >
          ← Back to Home
        </button>
        <AIChatbot />
      </>
    );
  }

  return (
    <div className="home">
      {/* Hero Section - Fully Responsive */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title" data-aos="fade-up">
            Hi, I'm <span className="highlight">Ben Sanga</span>
          </h1>
          <h2 className="hero-subtitle" data-aos="fade-up" data-aos-delay="100">
            I'm a{' '}
            <TypingEffect
              strings={[
                'Full-Stack Developer',
                'UI/UX Designer',
                'Web Solutions Provider',
                'Graphic Designer',
                'Django Expert',
                'React Developer'
              ]}
              typeSpeed={80}
              backSpeed={50}
              delay={1500}
            />
          </h2>
          <p className="hero-description" data-aos="fade-up" data-aos-delay="200">
            I build exceptional digital experiences that combine beautiful design with powerful functionality.
          </p>
          <div className="hero-buttons" data-aos="fade-up" data-aos-delay="300">
            <Link to="/projects" className="btn btn-primary">
              View My Work
            </Link>
            <Link to="/contact" className="btn btn-outline">
              Get In Touch
            </Link>
            <a 
              href="https://wa.me/256766670007" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-whatsapp"
            >
              <FaWhatsapp size={18} /> WhatsApp Me
            </a>
            <button 
              onClick={() => setShowChatbot(true)} 
              className="btn btn-chatbot"
            >
              🤖 AI Chatbot
            </button>
          </div>
        </div>
        <div className="hero-image" data-aos="fade-left">
          <div className="profile-wrapper">
            <img src="/sanga.png" alt="Sanga Ben" />
          </div>
        </div>
      </section>

      {/* Stats Section - Responsive Grid */}
      {stats && (
        <section className="stats">
          <div className="stats-grid">
            <div className="stat-item" data-aos="fade-up">
              <span className="stat-number">{stats.projects_count}+</span>
              <span className="stat-label">Projects</span>
            </div>
            <div className="stat-item" data-aos="fade-up" data-aos-delay="100">
              <span className="stat-number">{stats.services_count}</span>
              <span className="stat-label">Services</span>
            </div>
            <div className="stat-item" data-aos="fade-up" data-aos-delay="200">
              <span className="stat-number">{stats.skills_count}+</span>
              <span className="stat-label">Skills</span>
            </div>
            <div className="stat-item" data-aos="fade-up" data-aos-delay="300">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Support</span>
            </div>
          </div>
        </section>
      )}

      {/* AI Chatbot Preview Section - Responsive */}
      <section className="chatbot-preview">
        <div className="container">
          <div className="chatbot-preview-header" data-aos="fade-up">
            <div className="chatbot-badge">
              <span className="ai-badge">🤖 AI-POWERED CHATBOT</span>
            </div>
            <h2 className="section-title section-title-center">
              Ask Me Anything About My Work
            </h2>
            <p className="section-subtitle">
              Get instant answers about my skills, projects, experience, and availability
            </p>
          </div>
          
          <div className="chatbot-mini-preview" data-aos="fade-up" data-aos-delay="100">
            <div className="chatbot-preview-header-mini">
              <div className="chatbot-avatar">
                <Bot size={24} color="white" />
              </div>
              <div className="chatbot-info">
                <h4>Sanga AI Assistant</h4>
                <p>Online • Ready to help</p>
              </div>
              <div className="chatbot-status">
                <span className="online-dot"></span>
              </div>
            </div>
            
            <div className="chatbot-preview-messages">
              <div className="message bot">
                <div className="message-bubble">
                  👋 Hi! I'm Sanga's AI assistant. Ask me about:
                </div>
              </div>
              <div className="message bot">
                <div className="message-bubble">
                  • His skills (Django, Python, React)<br/>
                  • His projects (EcoHope, SangaBiz)<br/>
                  • His experience and education<br/>
                  • How to contact or hire him
                </div>
              </div>
              <div className="message user">
                <div className="message-bubble user-bubble">
                  What projects has he built?
                </div>
              </div>
              <div className="message bot">
                <div className="message-bubble">
                  Sanga has built EcoHope (agriculture platform), SangaBiz (business system), Edurise Initiative (education platform), and a music platform. Would you like details about any of these?
                </div>
              </div>
            </div>
            
            <div className="chatbot-preview-input">
              <input 
                type="text" 
                placeholder="Ask about Sanga's skills, projects..."
                readOnly
              />
              <button className="send-btn">
                <Send size={16} />
              </button>
            </div>
            
            <div className="chatbot-preview-actions">
              <button 
                onClick={() => setShowChatbot(true)} 
                className="btn btn-chatbot-full"
              >
                Launch Full AI Chatbot →
              </button>
              <a 
                href="https://wa.me/256766670007" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-whatsapp-chat"
              >
                <FaWhatsapp size={16} /> Or Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Responsive Grid */}
      <section className="services-preview">
        <div className="container">
          <h2 className="section-title section-title-center" data-aos="fade-up">
            What I Do
          </h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} delay={index * 100} />
            ))}
          </div>
          <div className="text-center" data-aos="fade-up">
            <Link to="/services" className="btn btn-outline">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Projects Section - Responsive Grid */}
      <section className="featured-projects">
        <div className="container">
          <h2 className="section-title section-title-center" data-aos="fade-up">
            Featured Projects
          </h2>
          <div className="projects-grid">
            {featuredProjects.map((project, index) => (
              <ProjectCard key={project.id} project={project} delay={index * 100} />
            ))}
          </div>
          <div className="text-center" data-aos="fade-up">
            <Link to="/projects" className="btn btn-primary">
              View All Projects
            </Link>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button - Responsive */}
      <a 
        href="https://wa.me/256766670007" 
        target="_blank" 
        rel="noopener noreferrer"
        className="whatsapp-float"
      >
        <FaWhatsapp size={28} />
      </a>

      {/* Call to Action Section - Responsive */}
      <section className="cta">
        <div className="container">
          <h2 data-aos="fade-up">Ready to Start Your Project?</h2>
          <p data-aos="fade-up" data-aos-delay="100">
            Let's work together to bring your ideas to life.
          </p>
          <div className="cta-buttons" data-aos="fade-up" data-aos-delay="200">
            <Link to="/contact" className="btn btn-light">
              Get In Touch
            </Link>
            <a 
              href="https://wa.me/256766670007" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-whatsapp-cta"
            >
              <FaWhatsapp size={16} /> WhatsApp Me
            </a>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* ============================================ */
        /* RESPONSIVE STYLES - Mobile First Approach */
        /* ============================================ */
        
        .chatbot-preview {
          padding: 60px 20px;
          background: linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%);
        }
        
        .dark .chatbot-preview {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        }
        
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .chatbot-badge {
          text-align: center;
          margin-bottom: 20px;
        }
        
        .ai-badge {
          display: inline-block;
          padding: 6px 14px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 20px;
          font-size: 12px;
          font-weight: bold;
          letter-spacing: 1px;
        }
        
        .section-title {
          font-size: 2rem;
          text-align: center;
          margin-bottom: 1rem;
        }
        
        .section-subtitle {
          text-align: center;
          color: #666;
          margin-top: 10px;
          font-size: 1rem;
        }
        
        .dark .section-subtitle {
          color: #aaa;
        }
        
        .chatbot-mini-preview {
          background: white;
          border-radius: 20px;
          padding: 0;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          max-width: 500px;
          margin: 0 auto;
          overflow: hidden;
        }
        
        .dark .chatbot-mini-preview {
          background: #1e293b;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        
        .chatbot-preview-header-mini {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: #1e3a5f;
          color: white;
        }
        
        .chatbot-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        .chatbot-info h4 {
          margin: 0;
          font-size: 14px;
          font-weight: bold;
        }
        
        .chatbot-info p {
          margin: 2px 0 0;
          font-size: 11px;
          opacity: 0.8;
        }
        
        .chatbot-status {
          margin-left: auto;
        }
        
        .online-dot {
          width: 10px;
          height: 10px;
          background: #10b981;
          border-radius: 50%;
          display: inline-block;
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .chatbot-preview-messages {
          padding: 20px;
          max-height: 300px;
          overflow-y: auto;
          background: #f8fafc;
        }
        
        .dark .chatbot-preview-messages {
          background: #0f172a;
        }
        
        .message {
          margin-bottom: 12px;
          display: flex;
        }
        
        .message.bot {
          justify-content: flex-start;
        }
        
        .message.user {
          justify-content: flex-end;
        }
        
        .message-bubble {
          max-width: 80%;
          padding: 10px 14px;
          border-radius: 12px;
          font-size: 13px;
          line-height: 1.5;
          background: white;
          color: #1f2937;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          border: 1px solid #e2e8f0;
          word-wrap: break-word;
        }
        
        .user-bubble {
          background: #2563eb;
          color: white;
          border: none;
        }
        
        .dark .message-bubble {
          background: #1e293b;
          color: #f1f5f9;
          border-color: #334155;
        }
        
        .chatbot-preview-input {
          padding: 16px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          gap: 8px;
        }
        
        .dark .chatbot-preview-input {
          background: #1e293b;
          border-top-color: #334155;
        }
        
        .chatbot-preview-input input {
          flex: 1;
          padding: 10px;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          background: #f8fafc;
          font-size: 13px;
        }
        
        .dark .chatbot-preview-input input {
          background: #0f172a;
          border-color: #334155;
          color: white;
        }
        
        .send-btn {
          padding: 8px 16px;
          background: #1e3a5f;
          color: white;
          border: none;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .send-btn:hover {
          background: #2563eb;
          transform: scale(1.05);
        }
        
        .chatbot-preview-actions {
          padding: 16px 20px 20px;
          background: white;
          display: flex;
          gap: 12px;
          justify-content: center;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
        }
        
        .dark .chatbot-preview-actions {
          background: #1e293b;
          border-top-color: #334155;
        }
        
        .btn-chatbot-full {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 10px 20px;
          border-radius: 30px;
          border: none;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          font-size: 13px;
        }
        
        .btn-whatsapp-chat {
          background: #25D366;
          color: white;
          padding: 10px 20px;
          border-radius: 30px;
          border: none;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          font-size: 13px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        
        .btn-chatbot-full:hover, .btn-whatsapp-chat:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }
        
        /* WhatsApp Floating Button */
        .whatsapp-float {
          position: fixed;
          bottom: 20px;
          left: 20px;
          background-color: #25D366;
          color: white;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 10px rgba(0,0,0,0.3);
          transition: all 0.3s ease;
          z-index: 100;
          text-decoration: none;
          cursor: pointer;
        }
        
        .whatsapp-float:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 15px rgba(0,0,0,0.4);
        }
        
        /* Hero Section Responsive */
        .hero {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 100px 5%;
          gap: 50px;
          flex-wrap: wrap;
        }
        
        .hero-content {
          flex: 1;
          min-width: 280px;
        }
        
        .hero-title {
          font-size: 3rem;
          margin-bottom: 1rem;
        }
        
        .hero-subtitle {
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }
        
        .hero-description {
          font-size: 1.1rem;
          margin-bottom: 2rem;
          color: #666;
        }
        
        .hero-image {
          flex: 1;
          display: flex;
          justify-content: center;
          min-width: 280px;
        }
        
        .profile-wrapper {
          width: 300px;
          height: 300px;
          border-radius: 50%;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
        }
        
        .profile-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .hero-buttons {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }
        
        .btn {
          padding: 12px 24px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          border: none;
        }
        
        .btn-primary {
          background: #2563eb;
          color: white;
        }
        
        .btn-primary:hover {
          background: #1d4ed8;
          transform: translateY(-2px);
        }
        
        .btn-outline {
          border: 2px solid #2563eb;
          color: #2563eb;
          background: transparent;
        }
        
        .btn-outline:hover {
          background: #2563eb;
          color: white;
          transform: translateY(-2px);
        }
        
        .btn-whatsapp {
          background: #25D366;
          color: white;
        }
        
        .btn-whatsapp:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(37, 211, 102, 0.4);
        }
        
        .btn-chatbot {
          background: #8b5cf6;
          color: white;
        }
        
        .btn-chatbot:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(139, 92, 246, 0.4);
        }
        
        .btn-light {
          background: white;
          color: #1e3a5f;
        }
        
        .btn-light:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
        }
        
        /* Stats Grid Responsive */
        .stats {
          padding: 60px 5%;
          background: #f8fafc;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .stat-item {
          text-align: center;
          padding: 20px;
        }
        
        .stat-number {
          font-size: 2.5rem;
          font-weight: bold;
          color: #2563eb;
          display: block;
        }
        
        .stat-label {
          font-size: 1rem;
          color: #666;
          margin-top: 10px;
          display: block;
        }
        
        /* Services & Projects Grid */
        .services-grid, .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
          margin: 40px 0;
        }
        
        .text-center {
          text-align: center;
        }
        
        /* CTA Section */
        .cta {
          padding: 80px 5%;
          background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
          color: white;
          text-align: center;
        }
        
        .cta h2 {
          font-size: 2rem;
          margin-bottom: 1rem;
        }
        
        .cta p {
          font-size: 1.1rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }
        
        .cta-buttons {
          display: flex;
          gap: 15px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .btn-whatsapp-cta {
          background: #25D366;
          color: white;
          padding: 12px 24px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
        }
        
        .btn-whatsapp-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(37, 211, 102, 0.4);
        }
        
        /* ============================================ */
        /* MEDIA QUERIES FOR RESPONSIVENESS */
        /* ============================================ */
        
        /* Tablet (768px and below) */
        @media (max-width: 768px) {
          .hero {
            flex-direction: column;
            text-align: center;
            padding: 60px 5%;
          }
          
          .hero-title {
            font-size: 2rem;
          }
          
          .hero-subtitle {
            font-size: 1.2rem;
          }
          
          .hero-description {
            font-size: 1rem;
          }
          
          .hero-buttons {
            justify-content: center;
          }
          
          .profile-wrapper {
            width: 250px;
            height: 250px;
          }
          
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }
          
          .stat-number {
            font-size: 2rem;
          }
          
          .section-title {
            font-size: 1.8rem;
          }
          
          .chatbot-preview {
            padding: 40px 15px;
          }
          
          .chatbot-preview-actions {
            flex-direction: column;
            align-items: center;
          }
          
          .btn-chatbot-full, .btn-whatsapp-chat {
            width: 100%;
            text-align: center;
            justify-content: center;
          }
          
          .services-grid, .projects-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .cta h2 {
            font-size: 1.5rem;
          }
          
          .cta p {
            font-size: 1rem;
          }
          
          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }
          
          .whatsapp-float {
            width: 45px;
            height: 45px;
            bottom: 15px;
            left: 15px;
          }
        }
        
        /* Mobile (480px and below) */
        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.8rem;
          }
          
          .hero-subtitle {
            font-size: 1rem;
          }
          
          .hero-buttons .btn {
            padding: 8px 16px;
            font-size: 14px;
          }
          
          .profile-wrapper {
            width: 200px;
            height: 200px;
          }
          
          .stats-grid {
            grid-template-columns: 1fr;
            gap: 15px;
          }
          
          .section-title {
            font-size: 1.5rem;
          }
          
          .section-subtitle {
            font-size: 0.9rem;
          }
          
          .chatbot-mini-preview {
            margin: 0 10px;
          }
          
          .message-bubble {
            max-width: 90%;
            font-size: 12px;
          }
          
          .chatbot-preview-header-mini {
            padding: 12px 15px;
          }
          
          .chatbot-avatar {
            width: 35px;
            height: 35px;
          }
          
          .chatbot-info h4 {
            font-size: 13px;
          }
          
          .chatbot-info p {
            font-size: 10px;
          }
          
          .btn-chatbot-full, .btn-whatsapp-chat {
            padding: 8px 16px;
            font-size: 12px;
          }
          
          .cta h2 {
            font-size: 1.3rem;
          }
          
          .cta p {
            font-size: 0.9rem;
          }
          
          .whatsapp-float {
            width: 40px;
            height: 40px;
            bottom: 10px;
            left: 10px;
          }
        }
        
        /* Large Desktop (1200px and above) */
        @media (min-width: 1200px) {
          .container {
            padding: 0;
          }
          
          .hero {
            padding: 120px 10%;
          }
          
          .hero-title {
            font-size: 3.5rem;
          }
          
          .profile-wrapper {
            width: 350px;
            height: 350px;
          }
        }
        
        /* Dark mode adjustments */
        .dark .stat-number {
          color: #60a5fa;
        }
        
        .dark .stat-label {
          color: #94a3b8;
        }
        
        .dark .hero-description {
          color: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default Home;